import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  Minus, 
  Plus, 
  ShoppingBag, 
  ArrowRight,
  ArrowLeft,
  MapPin,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { BASE_URL } from '../services/api';
import { removeFromCart, updateQuantity } from '../redux/slices/cartSlice';
import { isBranchOpen } from '../utils/branchUtils';

const Cart = () => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, totalAmount } = useSelector((state) => state.cart);
  const { selectedBranch } = useSelector((state) => state.branch);

  const isOpen = selectedBranch ? isBranchOpen(selectedBranch.operatingHours) : true;

  const shipping = totalAmount > 50 ? 0 : 5;
  const total = totalAmount + shipping;

  const renderName = (nameObj) => {
    if (typeof nameObj === 'string') return nameObj;
    return nameObj?.[i18n.language] || nameObj?.en || '';
  };

  const handleQuantityChange = (uniqueId, quantity) => {
    if (quantity < 1) return;
    dispatch(updateQuantity({ uniqueId, quantity }));
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 md:py-32 gap-6 animate-in fade-in px-4">
        <div 
          className="w-20 h-20 md:w-24 md:h-24 rounded-[32px] flex items-center justify-center" 
          style={{ backgroundColor: `${theme.primary}15` }}
        >
          <ShoppingBag size={40} md={48} style={{ color: theme.primary }} className="opacity-30" />
        </div>
        <h2 className="text-2xl md:text-3xl font-black uppercase tracking-widest text-center leading-tight">{t('cart.empty_title', 'Your cart is empty')}</h2>
        <p className="text-[10px] md:text-sm font-bold opacity-40 uppercase tracking-widest text-center">{t('cart.empty_subtitle', 'Add some coffee to get started')}</p>
        <Link 
          to="/products"
          className="px-8 md:px-10 py-4 md:py-5 rounded-[24px] font-black uppercase tracking-widest bg-black text-white shadow-xl transition-transform active:scale-95 text-xs md:text-base"
          style={{ backgroundColor: theme.primary, color: theme.background }}
        >
          {t('cart.explore_menu', 'Explore Menu')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-6xl mx-auto px-1 md:px-4">
      <div className="flex items-end justify-between px-2 md:px-0 gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl md:text-5xl font-black uppercase tracking-tighter leading-tight" style={{ color: theme.primary }}>{t('cart.title', 'My Cart')}</h1>
          <div className="flex items-center gap-2">
            <p className="text-[8px] md:text-sm font-bold uppercase tracking-widest opacity-40 leading-none">{items.length} {t('cart.items_count', 'unique items')}</p>
            {selectedBranch && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-black/5 text-[7px] md:text-[10px] font-black uppercase tracking-widest opacity-60">
                <MapPin size={8} md={10} style={{ color: theme.primary }} />
                <span>{renderName(selectedBranch.name)}</span>
              </div>
            )}
          </div>
        </div>
        <button 
          onClick={() => navigate('/products')}
          className="hidden md:flex items-center gap-2 text-xs font-black uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity whitespace-nowrap"
        >
          <ArrowLeft size={16} className="rtl:rotate-180" />
          <span>{t('cart.continue_shopping', 'Continue Shopping')}</span>
        </button>
      </div>

      {!isOpen && (
        <div className="mx-2 md:mx-0 p-4 md:p-6 rounded-[24px] md:rounded-[32px] bg-red-500/10 border border-red-500/20 flex flex-col md:flex-row items-center gap-4 text-center md:text-left shadow-lg">
          <div className="w-12 h-12 rounded-2xl bg-red-500/20 flex items-center justify-center shrink-0">
            <Clock size={24} className="text-red-500 animate-pulse" />
          </div>
          <div className="flex-grow space-y-1">
            <h4 className="text-sm md:text-lg font-black uppercase tracking-tight text-red-600 leading-none">
              {renderName(selectedBranch?.name)} {t('cart.is_closed', 'is Currently Closed')}
            </h4>
            <p className="text-[10px] md:text-sm font-bold uppercase tracking-widest text-red-500/60 leading-tight">
              {t('cart.closed_checkout_notice', 'Checkout is disabled because this branch is not receiving orders right now.')}
            </p>
          </div>
          <Link 
            to="/locations"
            className="px-6 py-3 rounded-xl bg-red-500 text-white font-black uppercase tracking-widest text-[10px] md:text-xs transition-transform active:scale-95"
          >
            {t('cart.view_hours', 'View Hours')}
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-12">
        {/* Cart Items */}
        <div className="lg:col-span-2 flex flex-col gap-4 md:gap-6">
          {items.map((item) => (
            <div 
              key={item.uniqueId}
              className="flex flex-col sm:flex-row items-center p-4 md:p-8 rounded-[24px] md:rounded-[40px] border transition-all hover:shadow-2xl gap-4 md:gap-8"
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <div className="w-full sm:w-32 h-40 sm:h-32 rounded-xl md:rounded-3xl overflow-hidden shrink-0" style={{ backgroundColor: theme.surface }}>
                <img 
                  src={item.image ? (item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`) : 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?q=80&w=100&auto=format&fit=crop'} 
                  alt={renderName(item.name)}
                  className={`w-full h-full object-cover ${!isOpen ? 'grayscale opacity-50' : ''}`}
                />
              </div>
              
              <div className="flex-grow flex flex-col gap-2 text-center sm:text-left min-w-0 w-full">
                <h3 className="text-lg md:text-xl font-black uppercase tracking-tight truncate leading-tight" style={{ color: theme.text }}>{renderName(item.name)}</h3>
                
                {/* Variant & Add-ons Display */}
                <div className="flex flex-wrap gap-1.5 justify-center sm:justify-start">
                  <span className="px-2 py-0.5 rounded-lg text-[7px] md:text-[10px] font-black uppercase tracking-widest bg-opacity-10" style={{ backgroundColor: theme.primary, color: theme.primary }}>
                    {item.variant?.name}
                  </span>
                  {item.selectedAddOns?.map(addOn => (
                    <span key={addOn._id} className="px-2 py-0.5 rounded-lg text-[7px] md:text-[10px] font-black uppercase tracking-widest opacity-40" style={{ backgroundColor: theme.surface }}>
                      + {addOn.name}
                    </span>
                  ))}
                </div>

                <p className="text-base md:text-lg font-black mt-2 leading-none" style={{ color: theme.accent }}>
                  {formatPrice(item.pricePerUnit)} <span className="text-[8px] md:text-[10px] opacity-40 font-bold uppercase tracking-widest ms-1">{t('cart.unit', 'unit')}</span>
                </p>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-4 md:gap-6 mt-4 sm:mt-0 w-full sm:w-auto border-t sm:border-t-0 pt-4 sm:pt-0" style={{ borderColor: theme.border }}>
                <div className="flex items-center p-1 md:p-2 rounded-xl md:rounded-2xl shrink-0" style={{ backgroundColor: theme.surface }}>
                  <button 
                    onClick={() => handleQuantityChange(item.uniqueId, item.quantity - 1)}
                    className="p-2 rounded-lg md:rounded-xl transition-colors hover:bg-black/5"
                  >
                    <Minus size={14} md={16} />
                  </button>
                  <span className="w-8 md:w-10 text-center font-black text-sm md:text-base">{item.quantity}</span>
                  <button 
                    onClick={() => handleQuantityChange(item.uniqueId, item.quantity + 1)}
                    className="p-2 rounded-lg md:rounded-xl transition-colors hover:bg-black/5"
                  >
                    <Plus size={14} md={16} />
                  </button>
                </div>
                
                <button 
                  onClick={() => dispatch(removeFromCart(item.uniqueId))}
                  className="p-3 md:p-4 rounded-xl md:rounded-2xl text-red-500 transition-all hover:bg-red-500 hover:bg-opacity-10 hover:scale-110 shadow-sm shrink-0"
                  style={{ backgroundColor: theme.surface }}
                >
                  <Trash2 size={16} md={20} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="flex flex-col gap-6">
          <div 
            className="p-6 md:p-10 rounded-[32px] md:rounded-[48px] shadow-2xl border lg:sticky lg:top-28"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-widest mb-6 md:mb-10 leading-none" style={{ color: theme.primary }}>{t('cart.summary', 'Order Summary')}</h2>
            
            <div className="flex flex-col gap-4 md:gap-5">
              <div className="flex justify-between text-[10px] md:text-xs font-black uppercase tracking-widest opacity-40">
                <span>{t('cart.subtotal', 'Subtotal')}</span>
                <span style={{ color: theme.text }}>{formatPrice(totalAmount)}</span>
              </div>
              <div className="flex justify-between text-[10px] md:text-xs font-black uppercase tracking-widest opacity-40">
                <span>{t('cart.shipping', 'Shipping')}</span>
                <span style={{ color: theme.text }}>{shipping === 0 ? t('cart.free', 'FREE') : formatPrice(shipping)}</span>
              </div>
              
              <div className="pt-4 md:pt-6 border-t" style={{ borderColor: theme.border }}>
                <div className="flex justify-between items-end gap-2">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-widest leading-none mb-1">{t('cart.total_amount', 'Total Amount')}</span>
                  <span className="text-2xl md:text-4xl font-black leading-none" style={{ color: theme.primary }}>{formatPrice(total)}</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => isOpen && navigate('/checkout')}
              disabled={!isOpen}
              className={`w-full mt-6 md:mt-10 py-4 md:py-6 rounded-2xl md:rounded-[24px] font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${
                isOpen 
                ? 'shadow-2xl active:scale-95' 
                : 'opacity-50 cursor-not-allowed'
              }`}
              style={{ 
                backgroundColor: isOpen ? theme.primary : theme.border, 
                color: isOpen ? theme.background : theme.text 
              }}
            >
              <span className="text-xs md:text-base">
                {isOpen ? t('cart.checkout', 'Go to Checkout') : t('cart.closed', 'Branch Closed')}
              </span>
              <ArrowRight size={18} md={20} className="rtl:rotate-180" />
            </button>

            {isOpen && shipping > 0 && (
              <p className="mt-4 md:mt-6 text-center text-[8px] md:text-[10px] font-black uppercase tracking-tighter text-amber-600 leading-tight">
                {t('cart.free_shipping_hint', 'Add more for free shipping!')}
              </p>
            )}
            
            {!isOpen && (
              <div className="mt-4 md:mt-6 flex items-center gap-2 justify-center text-red-500">
                <AlertCircle size={14} />
                <p className="text-[8px] md:text-[10px] font-black uppercase tracking-tighter leading-tight">
                  {t('cart.checkout_disabled_notice', 'Wait for opening to checkout')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
